import React, { useState, useEffect, useRef, } from 'react';
import { useNavigate } from 'react-router-dom';

import { Search, Filter, SlidersHorizontal, AlertTriangle, ChevronDown, ChevronUp, MoreVertical, Plus, Eye, Edit, Trash2 } from 'lucide-react';

const API_BASE_URL = 'https://ior.org/risk-register/web/api';

const RiskListingView = () => {
  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [categories, setCategories] = useState([]);

  const [actionMenuOpen, setActionMenuOpen] = useState(null);
  const menuRef = useRef(null);
   const navigate = useNavigate();
   
  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setActionMenuOpen(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Action handlers
  const handleAddNew = () => {
    navigate('/risks/new');
  };

  const handleViewRisk = (risk) => {
    navigate(`/risks/${risk.risk_code}`);
  };

  const handleEditRisk = (risk) => {
    navigate(`/risks/${risk.risk_code}/edit`);
  };

  const handleDeleteRisk = async (risk) => {
    if (window.confirm('Are you sure you want to delete this risk?')) {
      try {
        const response = await fetch(`${API_BASE_URL}/risks.php?id=${risk.risk_code}`, {
          method: 'DELETE'
        });
        const data = await response.json();
        
        if (data.success) {
          // Remove risk from the list
          setRisks(current => current.filter(r => r.risk_code !== risk.risk_code));
        } else {
          throw new Error(data.error || 'Failed to delete risk');
        }
      } catch (err) {
        setError(err.message);
      }
    }
  };

  // Actions Menu Component
  const ActionsMenu = ({ risk }) => {
    if (actionMenuOpen !== risk.risk_code) return null;

    return (
      <div 
        ref={menuRef}
        className="fixed right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10"
      >
        <div className="py-1" role="menu">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleViewRisk(risk);
            }}
            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
          >
            <Eye className="w-4 h-4 mr-2" />
            View Details
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleEditRisk(risk);
            }}
            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
          >
            <Edit className="w-4 h-4 mr-2" />
            Edit Risk
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteRisk(risk);
            }}
            className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete Risk
          </button>
        </div>
      </div>
    );
  };

  // Fetch risks and categories
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch risks
        const risksResponse = await fetch(`${API_BASE_URL}/risks.php`);
        const risksData = await risksResponse.json();
        
        if (risksData.success) {
          setRisks(risksData.data);
        } else {
          throw new Error(risksData.error || 'Failed to fetch risks');
        }

        // Fetch categories
        const categoriesResponse = await fetch(`${API_BASE_URL}/risk-categories.php`);
        const categoriesData = await categoriesResponse.json();
        
        if (categoriesData.success) {
          setCategories(categoriesData.data);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Status badge component
  const StatusBadge = ({ status }) => {
    const getStatusInfo = () => {
      const riskScore = parseInt(status);
      if (riskScore >= 16) return { style: 'high', label: 'High Risk' };
      if (riskScore >= 9) return { style: 'medium', label: 'Medium Risk' };
      return { style: 'low', label: 'Low Risk' };
    };

    const statusStyles = {
      high: 'bg-red-100 text-red-800 border-red-200',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      low: 'bg-green-100 text-green-800 border-green-200'
    };

    const { style, label } = getStatusInfo();
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusStyles[style]}`}>
        {label}
      </span>
    );
  };

  // Status badge component
  const ImpactBadge = ({ status }) => {
    const getStatusInfo = () => {
      const riskScore = parseInt(status);
      if(riskScore === 1) return { style: 'low', label: 'Very Low' };
      if(riskScore === 2) return { style: 'low', label: 'Low' };
      if(riskScore === 3) return { style: 'medium', label: 'Medium' };
      if(riskScore === 4) return { style: 'high', label: 'High' };
      if(riskScore === 5) return { style: 'high', label: 'Very High' };

      
      return { style: 'low', label: 'Low Risk' };
    };

    const statusStyles = {
      high: 'bg-red-100 text-red-800 border-red-200',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      low: 'bg-green-100 text-green-800 border-green-200'
    };

    const { style, label } = getStatusInfo();
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusStyles[style]}`}>
        {label}
      </span>
    );
  };

  // Approval status badge component
  const ApprovalBadge = ({ status }) => {
    const approvalStyles = {
      'pending-smt': 'bg-blue-100 text-blue-800 border-blue-200',
      'pending-gb': 'bg-purple-100 text-purple-800 border-purple-200',
      'approved': 'bg-green-100 text-green-800 border-green-200',
      'rejected': 'bg-red-100 text-red-800 border-red-200'
    };

    const approvalLabels = {
      'pending-smt': 'Pending SMT',
      'pending-gb': 'Pending GB',
      'approved': 'Approved',
      'rejected': 'Rejected'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${approvalStyles[status]}`}>
        {approvalLabels[status]}
      </span>
    );
  };

  // Sorting function
  const sortRisks = (risks) => {
    return [...risks].sort((a, b) => {
      if (sortConfig.key === 'date') {
        return sortConfig.direction === 'asc' 
          ? new Date(a.created_at) - new Date(b.created_at)
          : new Date(b.created_at) - new Date(a.created_at);
      }
      
      // Handle risk_code sorting
      if (sortConfig.key === 'id') {
        const aNum = parseInt(a.risk_code.slice(1));
        const bNum = parseInt(b.risk_code.slice(1));
        return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
      }
      
      return sortConfig.direction === 'asc'
        ? (a[sortConfig.key] || '').toString().localeCompare((b[sortConfig.key] || '').toString())
        : (b[sortConfig.key] || '').toString().localeCompare((a[sortConfig.key] || '').toString());
    });
  };

  // Filtering function
  const filterRisks = (risks) => {
    return risks.filter(risk => {
      const matchesSearch = 
        risk.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        risk.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        risk.risk_code.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesCategory = selectedCategory === 'all' || risk.category_id.toString() === selectedCategory;
      const riskLevel = getRiskLevel(risk.risk_score);
      const matchesStatus = selectedStatus === 'all' || riskLevel === selectedStatus;
      
      return matchesSearch && matchesCategory && matchesStatus;
    });
  };

  // Get risk level based on risk score
  const getRiskLevel = (score) => {
    if (score >= 16) return 'high';
    if (score >= 9) return 'medium';
    return 'low';
  };

  // Handle sort
  const handleSort = (key) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Navigation handler
  const handleNavigate = (risk_code) => {
    // TODO: Implement navigation to risk detail view
    console.log('Navigate to risk:', risk_code);
  };



  // Process risks through sorting and filtering
  const processedRisks = loading ? [] : sortRisks(filterRisks(risks));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Risk Register</h2>
        <button 
          onClick={handleAddNew}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add New Risk
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-50 border-l-4 border-red-400 text-red-700">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 mr-2" />
            {error}
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search risks..."
            className="pl-10 pr-4 py-2 w-full border rounded-md"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex gap-4">
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <select
              className="pl-10 pr-4 py-2 border rounded-md appearance-none bg-white"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <option value="all">All Categories</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>
          <div className="relative">
            <SlidersHorizontal className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <select
              className="pl-10 pr-4 py-2 border rounded-md appearance-none bg-white"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              <option value="all">All Risk Levels</option>
              <option value="high">High Risk</option>
              <option value="medium">Medium Risk</option>
              <option value="low">Low Risk</option>
            </select>
          </div>
        </div>
      </div>

      {/* Risk Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white shadow-sm rounded-lg">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <button
                  onClick={() => handleSort('id')}
                  className="flex items-center space-x-1"
                >
                  <span>Risk ID</span>
                  {sortConfig.key === 'id' && (
                    sortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                  )}
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <button
                  onClick={() => handleSort('title')}
                  className="flex items-center space-x-1"
                >
                  <span>Title</span>
                  {sortConfig.key === 'title' && (
                    sortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                  )}
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Category
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Risk Level
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Risk Impact
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Resolved
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <button
                  onClick={() => handleSort('date')}
                  className="flex items-center space-x-1"
                >
                  <span>Date</span>
                  {sortConfig.key === 'date' && (
                    sortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                  )}
                </button>
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                  Loading risks...
                </td>
              </tr>
            ) : processedRisks.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                  No risks found
                </td>
              </tr>
            ) : (
              processedRisks.map((risk) => (
                <tr 
                  key={risk.risk_code} 
                  className="hover:bg-gray-50"
                  onClick={() => handleViewRisk(risk)}
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {risk.risk_code}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{risk.title}</div>
                    <div className="text-sm text-gray-500">{risk.description}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {categories.find(c => c.id === risk.category_id)?.label || risk.category_id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <StatusBadge status={risk.risk_score} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <ImpactBadge status={risk.impact} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <ApprovalBadge status={risk.approval_status} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(risk.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium relative">
                    <button 
                      className="text-gray-400 hover:text-gray-600"
                      onClick={(e) => {
                        e.stopPropagation();
                        setActionMenuOpen(actionMenuOpen === risk.risk_code ? null : risk.risk_code);
                      }}
                    >
                      <MoreVertical className="w-5 h-5" />
                    </button>
                    <ActionsMenu risk={risk} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RiskListingView;
